/**
 * WorkflowTestRunner - Real-time workflow execution panel with SSE streaming
 * Shows per-node status, output, duration, and cost.
 */
import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Play,
  Square,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  CheckCircle2,
  XCircle,
  Loader2,
  SkipForward,
  Copy,
  Save,
  RotateCcw,
  FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';

export type StepStatus = 'idle' | 'running' | 'success' | 'error' | 'skipped';

export interface StepResult {
  step_index: number;
  step_name: string;
  provider: string;
  model?: string;
  status: StepStatus;
  result?: any;
  error?: string;
  duration_ms?: number;
  tokens_used?: number;
  cost_usd?: number;
}

export interface ExecutionState {
  status: 'idle' | 'running' | 'complete' | 'error';
  stepResults: StepResult[];
  totalDuration?: number;
  error?: string;
}

interface WorkflowTestRunnerProps {
  agent: any;
  /** Called when execution state changes (for node animation) */
  onExecutionEvent?: (event: any) => void;
  /** Current workflow to use for streaming test (converted from flow) */
  currentWorkflow?: any;
}

const API_BASE = '';

/** Sanitize execution results for safe clipboard copy (strip secrets/internal URLs) */
function sanitizeForClipboard(data: any): any {
  const str = JSON.stringify(data, null, 2);
  return str
    .replace(/eyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}/g, '[REDACTED_JWT]')
    .replace(/mongodb\+srv:\/\/[^\s"'`]+/gi, '[REDACTED_URI]')
    .replace(/mongodb:\/\/[^\s"'`]+/gi, '[REDACTED_URI]')
    .replace(/(?:sk-|pk-)[a-zA-Z0-9]{20,}/g, '[REDACTED_KEY]')
    .replace(/"[Aa]uthorization"\s*:\s*"[^"]+"/g, '"Authorization": "[REDACTED]"')
    .replace(/"[Bb]earer\s+[^"]+"/g, '"Bearer [REDACTED]"')
    .replace(/http:\/\/(?:devcockpit-|agent-creator|volcano-engine|orchestrator)[^"'\s]*/g, '[INTERNAL_URL]');
}

export default function WorkflowTestRunner({ agent, onExecutionEvent, currentWorkflow }: WorkflowTestRunnerProps) {
  // Load initial input from test_fixtures if available
  const getDefaultInput = () => {
    const fixtures = agent?.test_fixtures;
    if (fixtures) {
      // Try current workflow first, then 'default', then first available
      const workflowName = currentWorkflow?.name || 'default';
      const fixture = fixtures[workflowName] || fixtures.default || Object.values(fixtures)[0];
      if (fixture) {
        const input = Array.isArray(fixture) ? fixture[0]?.input : fixture.input;
        if (input) {
          return typeof input === 'string' ? JSON.stringify(input) : JSON.stringify(input, null, 2);
        }
      }
    }
    return '{\n  "test": true,\n  "message": "Test from Workflow Designer"\n}';
  };

  const [inputJson, setInputJson] = useState(getDefaultInput());
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [execution, setExecution] = useState<ExecutionState>({ status: 'idle', stepResults: [] });
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const eventSourceRef = useRef<EventSource | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const validateJson = (text: string): boolean => {
    try {
      JSON.parse(text);
      setJsonError(null);
      return true;
    } catch (e: any) {
      setJsonError(e.message);
      return false;
    }
  };

  const handleInputChange = (value: string) => {
    setInputJson(value);
    if (value.trim()) validateJson(value);
    else setJsonError(null);
  };

  const stopExecution = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setExecution(prev => ({
      ...prev,
      status: prev.status === 'running' ? 'error' : prev.status,
      error: prev.status === 'running' ? 'Execution cancelled by user' : prev.error,
    }));
  }, []);

  const runTest = useCallback(async () => {
    if (!validateJson(inputJson)) {
      toast.error('Invalid JSON input');
      return;
    }

    const parsedInput = JSON.parse(inputJson);
    setExecution({ status: 'running', stepResults: [] });
    setExpandedSteps(new Set());

    // Build agent definition for streaming execution
    const workflow = currentWorkflow || agent?.volcano_workflow;
    if (!workflow || !workflow.steps || workflow.steps.length === 0) {
      toast.error('No workflow steps to execute');
      setExecution({ status: 'error', stepResults: [], error: 'No workflow steps found' });
      return;
    }

    // Number the steps
    const numberedSteps = workflow.steps.map((s: any, i: number) => ({
      ...s,
      step_number: i + 1,
      name: s.name || `Step ${i + 1}`,
    }));

    const agentDef = {
      id: agent?.id || 'test-agent',
      name: agent?.name || 'Test Agent',
      volcano_workflow: { steps: numberedSteps },
    };

    // Notify parent about start
    onExecutionEvent?.({ type: 'start', total_steps: numberedSteps.length });

    try {
      const abort = new AbortController();
      abortRef.current = abort;

      const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      try {
        const auth = JSON.parse(localStorage.getItem('dc.auth') || '{}');
        if (auth.token) authHeaders['Authorization'] = `Bearer ${auth.token}`;
        authHeaders['x-tenant-id'] = auth.tenant || auth.tenantId || 'dev';
        authHeaders['x-app-id'] = auth.app_id || auth.appId || localStorage.getItem('dc.app_id') || '';
      } catch { /* use defaults */ }
      const response = await fetch(`${API_BASE}/cp/api/volcano/execute/stream`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          agent_definition: agentDef,
          input: parsedInput,
          context: { test_run: true },
        }),
        signal: abort.signal,
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Server error ${response.status}: ${errText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);
            handleSSEEvent(event);
            onExecutionEvent?.(event);
          } catch {
            // Skip unparseable events
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return; // User cancelled
      console.error('Streaming execution error:', err);
      setExecution(prev => ({
        ...prev,
        status: 'error',
        error: err.message,
      }));
      onExecutionEvent?.({ type: 'error', error: err.message });
      toast.error(`Execution failed: ${err.message}`);
    }
  }, [inputJson, agent, currentWorkflow, onExecutionEvent]);

  const handleSSEEvent = (event: any) => {
    switch (event.type) {
      case 'start':
        // Initialize step placeholders
        setExecution({
          status: 'running',
          stepResults: (event.step_names || []).map((name: string, i: number) => ({
            step_index: i,
            step_name: name,
            provider: '',
            status: 'idle' as StepStatus,
          })),
        });
        break;

      case 'step_start':
        setExecution(prev => ({
          ...prev,
          stepResults: prev.stepResults.map((sr, i) =>
            i === event.step_index
              ? { ...sr, status: 'running', provider: event.provider, model: event.model }
              : sr
          ),
        }));
        setExpandedSteps(prev => new Set([...prev, event.step_index]));
        break;

      case 'step_complete':
        setExecution(prev => ({
          ...prev,
          stepResults: prev.stepResults.map((sr, i) =>
            i === event.step_index
              ? {
                  ...sr,
                  status: 'success',
                  result: event.result,
                  duration_ms: event.duration_ms,
                  tokens_used: event.tokens_used,
                  cost_usd: event.cost_usd,
                }
              : sr
          ),
        }));
        break;

      case 'step_error':
        setExecution(prev => ({
          ...prev,
          stepResults: prev.stepResults.map((sr, i) =>
            i === event.step_index
              ? { ...sr, status: 'error', error: event.error, duration_ms: event.duration_ms }
              : sr
          ),
        }));
        break;

      case 'step_skipped':
        setExecution(prev => ({
          ...prev,
          stepResults: prev.stepResults.map((sr, i) =>
            i === event.step_index
              ? { ...sr, status: 'skipped', provider: event.provider }
              : sr
          ),
        }));
        break;

      case 'complete':
        setExecution(prev => ({
          ...prev,
          status: 'complete',
          totalDuration: event.duration_ms,
        }));
        toast.success(`Workflow completed in ${(event.duration_ms / 1000).toFixed(1)}s`);
        break;

      case 'error':
        setExecution(prev => ({
          ...prev,
          status: 'error',
          error: event.error,
          totalDuration: event.duration_ms,
        }));
        toast.error(`Workflow failed: ${event.error}`);
        break;
    }
  };

  const toggleStep = (index: number) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const statusIcon = (status: StepStatus) => {
    switch (status) {
      case 'running': return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'success': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'skipped': return <SkipForward className="w-4 h-4 text-gray-400" />;
      default: return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />;
    }
  };

  const copyResult = (result: any) => {
    navigator.clipboard.writeText(sanitizeForClipboard(result));
    toast.success('Copied to clipboard (sanitized)');
  };

  const copyFullResult = () => {
    const bundle = {
      agent_id: agent?.id || 'unknown',
      workflow: currentWorkflow?.name || 'default',
      input: (() => { try { return JSON.parse(inputJson); } catch { return inputJson; } })(),
      execution_status: execution.status,
      steps: execution.stepResults.map(sr => ({
        step_name: sr.step_name,
        provider: sr.provider,
        status: sr.status,
        result: sr.result,
        error: sr.error,
        duration_ms: sr.duration_ms,
        tokens_used: sr.tokens_used,
      })),
      total_duration_ms: execution.totalDuration,
      error: execution.error,
    };
    navigator.clipboard.writeText(sanitizeForClipboard(bundle));
    toast.success('Full result copied (sanitized)');
  };

  const totalCost = execution.stepResults.reduce((sum, sr) => sum + (sr.cost_usd || 0), 0);
  const totalTokens = execution.stepResults.reduce((sum, sr) => sum + (sr.tokens_used || 0), 0);

  return (
    <div className="w-[400px] border-l border-gray-200 bg-gray-50 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b bg-white flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-sm">Test Runner</span>
        </div>
        {execution.status === 'running' && (
          <Button size="sm" variant="destructive" onClick={stopExecution} className="h-7 text-xs">
            <Square className="w-3 h-3 mr-1" /> Stop
          </Button>
        )}
      </div>

      {/* Input Section */}
      <div className="p-3 border-b bg-white shrink-0">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-700">Test Input (JSON)</label>
          {/* Test Fixtures Dropdown */}
          {agent?.test_fixtures && Object.keys(agent.test_fixtures).length > 0 && (
            <select
              className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5 text-gray-600 bg-white focus:ring-1 focus:ring-purple-300 focus:outline-none"
              defaultValue=""
              onChange={(e) => {
                const fixtureKey = e.target.value;
                if (!fixtureKey) return;
                const fixture = agent.test_fixtures[fixtureKey];
                const input = Array.isArray(fixture) ? fixture[0]?.input : fixture?.input;
                if (input) {
                  const json = typeof input === 'string' ? JSON.stringify(input) : JSON.stringify(input, null, 2);
                  setInputJson(json);
                  setJsonError(null);
                  toast.success(`Loaded fixture: ${Array.isArray(fixture) ? fixture[0]?.label : fixture?.label || fixtureKey}`);
                }
                e.target.value = '';
              }}
            >
              <option value="">Load fixture...</option>
              {Object.entries(agent.test_fixtures).map(([key, fixture]: [string, any]) => (
                <option key={key} value={key}>
                  {Array.isArray(fixture) ? fixture[0]?.label || key : fixture?.label || key}
                </option>
              ))}
            </select>
          )}
        </div>
        <textarea
          value={inputJson}
          onChange={(e) => handleInputChange(e.target.value)}
          className={`w-full h-28 text-xs font-mono p-2 border rounded-md resize-none focus:outline-none focus:ring-2 ${
            jsonError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
          }`}
          placeholder='{ "key": "value" }'
          spellCheck={false}
        />
        {jsonError && <p className="text-xs text-red-500 mt-1">{jsonError}</p>}

        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            onClick={runTest}
            disabled={execution.status === 'running' || !!jsonError}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
          >
            <Play className="w-3 h-3 mr-1" />
            {execution.status === 'running' ? 'Running...' : 'Run Test'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setExecution({ status: 'idle', stepResults: [] });
              onExecutionEvent?.({ type: 'reset' });
            }}
            className="h-8 text-xs"
          >
            <RotateCcw className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Execution Results */}
      <div className="flex-1 overflow-y-auto">
        {execution.stepResults.length === 0 && execution.status === 'idle' && (
          <div className="p-8 text-center text-gray-400 text-sm">
            <FlaskConical className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>Enter test input and click Run to execute the workflow</p>
          </div>
        )}

        {execution.stepResults.map((sr, index) => (
          <div key={index} className={`border-b border-gray-200 ${sr.status === 'running' ? 'bg-blue-50' : ''}`}>
            {/* Step Header */}
            <button
              className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-gray-100 transition-colors"
              onClick={() => toggleStep(index)}
            >
              {statusIcon(sr.status)}
              <span className="flex-1 text-xs font-medium text-gray-900 truncate">
                {sr.step_name}
              </span>
              {sr.provider && (
                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                  {sr.provider}
                </Badge>
              )}
              {sr.duration_ms != null && (
                <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                  <Clock className="w-3 h-3" />
                  {sr.duration_ms < 1000 ? `${sr.duration_ms}ms` : `${(sr.duration_ms / 1000).toFixed(1)}s`}
                </span>
              )}
              {expandedSteps.has(index) ? (
                <ChevronDown className="w-3 h-3 text-gray-400" />
              ) : (
                <ChevronRight className="w-3 h-3 text-gray-400" />
              )}
            </button>

            {/* Step Details (expanded) */}
            {expandedSteps.has(index) && (
              <div className="px-3 pb-3 space-y-2">
                {sr.model && (
                  <div className="text-[10px] text-gray-500">
                    Model: <span className="font-mono">{sr.model}</span>
                  </div>
                )}

                {sr.status === 'running' && (
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Executing...
                  </div>
                )}

                {sr.error && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
                    {sr.error}
                  </div>
                )}

                {sr.result != null && (
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-medium text-gray-600">Output:</span>
                      <button
                        className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                        onClick={() => copyResult(sr.result)}
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-green-400 p-2 rounded text-[10px] font-mono overflow-x-auto max-h-40 overflow-y-auto">
                      {typeof sr.result === 'string' ? sr.result : JSON.stringify(sr.result, null, 2)}
                    </pre>
                  </div>
                )}

                {(sr.tokens_used || sr.cost_usd) && (
                  <div className="flex gap-3 text-[10px] text-gray-500">
                    {sr.tokens_used && <span>Tokens: {sr.tokens_used.toLocaleString()}</span>}
                    {sr.cost_usd && <span>Cost: ${sr.cost_usd.toFixed(4)}</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary Footer */}
      {execution.status !== 'idle' && (
        <div className={`p-3 border-t shrink-0 ${
          execution.status === 'complete' ? 'bg-green-50' :
          execution.status === 'error' ? 'bg-red-50' :
          'bg-blue-50'
        }`}>
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              {execution.status === 'running' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
              {execution.status === 'complete' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
              {execution.status === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
              <span className="font-medium">
                {execution.status === 'running' && 'Executing...'}
                {execution.status === 'complete' && 'Complete'}
                {execution.status === 'error' && 'Failed'}
              </span>
            </div>
            <div className="flex gap-3 text-gray-600">
              {execution.totalDuration != null && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {(execution.totalDuration / 1000).toFixed(1)}s
                </span>
              )}
              {totalTokens > 0 && <span>{totalTokens.toLocaleString()} tokens</span>}
              {totalCost > 0 && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />${totalCost.toFixed(4)}
                </span>
              )}
            </div>
          </div>
          {execution.error && (
            <p className="text-xs text-red-600 mt-1 truncate">{execution.error}</p>
          )}
          {(execution.status === 'complete' || execution.status === 'error') && (
            <button
              onClick={copyFullResult}
              className="mt-2 w-full flex items-center justify-center gap-1.5 text-[10px] font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-md py-1.5 hover:bg-gray-50 transition-colors"
            >
              <Copy className="w-3 h-3" />
              Copy Full Result (for debugging)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
